import { PageHelp } from "@/components/PageHelp";
import { CardSkeleton, Skeleton } from "@/components/Skeleton";
import { mediaApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { G1_ZONE_META, Page } from "@theg1group/ui";
import { LayoutDashboard } from "lucide-react";

type PaginatedResponse<T> = {
	data: T[];
	meta: { total: number };
};

type Entity = { id: string; name: string; updatedAt: string };
type Review = { id: string; title: string; updatedAt: string };

const ZONE = G1_ZONE_META.console;

export function Dashboard() {
	const { user } = useAuth();

	const entitiesQuery = useQuery({
		queryKey: ["entities", "stats"],
		queryFn: () => mediaApi.get<PaginatedResponse<Entity>>("/api/v1/entities?limit=5&page=1"),
	});

	const reviewsQuery = useQuery({
		queryKey: ["reviews", "stats"],
		queryFn: () => mediaApi.get<PaginatedResponse<Review>>("/api/v1/reviews?limit=5&page=1"),
	});

	const entityCount = entitiesQuery.data?.meta.total ?? "--";
	const reviewCount = reviewsQuery.data?.meta.total ?? "--";
	const recentEntities = entitiesQuery.data?.data ?? [];
	const recentReviews = reviewsQuery.data?.data ?? [];
	const isLoading = entitiesQuery.isLoading || reviewsQuery.isLoading;

	return (
		<Page>
			{/* Zone hero — explicit name + purpose so the SPA never gets
			    confused with admin.g1.network. Source of truth lives in
			    packages/ui/src/apps.ts (G1_ZONE_META). */}
			<div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
					<LayoutDashboard size={14} />
					{ZONE.app.name} zone · {ZONE.host}
				</div>
				<h1 className="mt-2 text-2xl font-bold text-content">
					Welcome back{user?.name ? `, ${user.name}` : ""}.
				</h1>
				<p className="mt-1 text-sm text-muted">{ZONE.purpose}</p>
				<PageHelp page="dashboard" />
			</div>

			{/* Stat cards */}
			<div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
				{isLoading ? (
					<>
						<CardSkeleton />
						<CardSkeleton />
						<CardSkeleton />
					</>
				) : (
					<>
						<StatCard
							title="Total Entities"
							value={String(entityCount)}
							subtitle="All entity types"
						/>
						<StatCard title="Total Reviews" value={String(reviewCount)} subtitle="All statuses" />
						<StatCard title="Your Role" value={user?.role ?? "--"} subtitle={user?.email ?? ""} />
					</>
				)}
			</div>

			{/* Recent activity */}
			<div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
				<RecentList
					title="Recent Entities"
					isLoading={entitiesQuery.isLoading}
					isError={entitiesQuery.isError}
					items={recentEntities.map((e) => ({
						id: e.id,
						label: e.name,
						date: e.updatedAt,
					}))}
					emptyMessage="No entities yet."
				/>
				<RecentList
					title="Recent Reviews"
					isLoading={reviewsQuery.isLoading}
					isError={reviewsQuery.isError}
					items={recentReviews.map((r) => ({
						id: r.id,
						label: r.title,
						date: r.updatedAt,
					}))}
					emptyMessage="No reviews yet."
				/>
			</div>
		</Page>
	);
}

function StatCard({
	title,
	value,
	subtitle,
}: {
	title: string;
	value: string;
	subtitle: string;
}) {
	return (
		<div className="rounded-lg border border-line bg-surface p-6">
			<h3 className="text-sm font-medium text-muted">{title}</h3>
			<p className="mt-2 text-3xl font-bold text-content">{value}</p>
			<p className="mt-1 text-xs text-muted">{subtitle}</p>
		</div>
	);
}

function RecentList({
	title,
	isLoading,
	isError,
	items,
	emptyMessage,
}: {
	title: string;
	isLoading: boolean;
	isError: boolean;
	items: { id: string; label: string; date: string }[];
	emptyMessage: string;
}) {
	return (
		<div>
			<h2 className="mb-3 text-sm font-semibold text-content">{title}</h2>
			<div className="rounded-lg border border-line bg-surface">
				{isLoading && (
					<div className="space-y-3 p-4">
						{Array.from({ length: 3 }).map((_, i) => (
							<div key={`sk-${i}`} className="flex items-center justify-between">
								<Skeleton className="h-4 w-40" />
								<Skeleton className="h-3 w-16" />
							</div>
						))}
					</div>
				)}
				{isError && <div className="p-4 text-sm text-danger">Failed to load data.</div>}
				{!isLoading && !isError && items.length === 0 && (
					<div className="p-4 text-sm text-muted">{emptyMessage}</div>
				)}
				{!isLoading &&
					!isError &&
					items.map((item) => (
						<div
							key={item.id}
							className="flex items-center justify-between border-b border-line
								px-4 py-3 last:border-b-0"
						>
							<span className="text-sm text-content">{item.label}</span>
							<span className="text-xs text-muted">
								{new Date(item.date).toLocaleDateString()}
							</span>
						</div>
					))}
			</div>
		</div>
	);
}
